/**
 * Google Analytics 4 Plugin
 *
 * Enables site visitor tracking with GA4.
 * Configure by setting VITE_GA_PROPERTY_ID environment variable.
 *
 * The property ID should be just the number (e.g., "123456789" or "G-XXXXXXXXXX")
 */
import type { Router } from 'vue-router'

// Extend window type for gtag
declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

/**
 * Initialize Google Analytics 4
 * @param propertyId GA4 property ID (e.g., "G-XXXXXXXXXX" or just "123456789")
 */
export function initGA4(propertyId: string): void {
  if (!propertyId || typeof window === 'undefined') {
    return
  }

  // Normalize property ID - if it's just numbers, prepend G-
  const gaId = propertyId.startsWith('G-') ? propertyId : `G-${propertyId}`

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || []

  // Define gtag function
  window.gtag = function (...args: unknown[]) {
    window.dataLayer.push(args)
  }

  // Set initial timestamp
  window.gtag('js', new Date())

  // Configure GA4
  window.gtag('config', gaId, {
    send_page_view: false, // We'll send page views manually on route changes
  })

  // Inject the gtag.js script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`
  document.head.appendChild(script)

  console.log(`[GA4] Initialized with property: ${gaId}`)
}

/**
 * Track a page view
 * @param path Page path (e.g., "/contacts")
 * @param title Page title
 */
export function trackPageView(path: string, title?: string): void {
  if (typeof window.gtag !== 'function') {
    return
  }

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  })
}

/**
 * Track a custom event
 * @param eventName Event name (e.g., "sign_up", "contact_created")
 * @param params Event parameters
 */
export function trackEvent(eventName: string, params?: Record<string, unknown>): void {
  if (typeof window.gtag !== 'function') {
    return
  }

  window.gtag('event', eventName, params)
}

/**
 * Setup automatic page view tracking on route changes
 * @param router Vue Router instance
 */
export function setupRouterTracking(router: Router): void {
  router.afterEach((to) => {
    // Track page view after navigation
    trackPageView(to.fullPath, to.meta?.title as string)
  })
}

/**
 * Vue plugin for GA4 analytics
 *
 * Usage in main.ts:
 * ```ts
 * import { createAnalyticsPlugin } from './plugins/analytics'
 *
 * const app = createApp(App)
 * app.use(createAnalyticsPlugin(router))
 * ```
 */
export function createAnalyticsPlugin(router: Router) {
  return {
    install() {
      const propertyId = import.meta.env.VITE_GA_PROPERTY_ID

      if (propertyId) {
        initGA4(propertyId)
        setupRouterTracking(router)
      } else {
        console.log('[GA4] No property ID configured. Set VITE_GA_PROPERTY_ID to enable tracking.')
      }
    },
  }
}

// Export individual functions for manual use
export const analytics = {
  trackPageView,
  trackEvent,
}

export default analytics
